import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MediaUploader } from "./MediaUploader";
import { useState } from "react";
import { useTable } from "@refinedev/core";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface MediaFile {
  id: number;
  url: string;
  originalName: string;
  mimeType: string;
  size: number;
}

interface TableResult {
  tableQueryResult?: {
    data?: {
      data?: MediaFile[];
    };
  };
  setFilters: (filters: Array<{ field: string; operator: string; value: string }>) => void;
}

export const MediaLibraryModal = ({
  onSelect,
  trigger,
}: {
  onSelect: (file: MediaFile) => void;
  trigger?: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const { tableQueryResult, setFilters } = useTable({
    resource: "files/admin",
    pagination: { pageSize: 12 },
    syncWithLocation: false,
  }) as TableResult;

  const files = tableQueryResult?.data?.data || [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button variant="outline">Select Media</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col bg-white dark:bg-zinc-900">
        <DialogHeader>
          <DialogTitle>Media Library</DialogTitle>
        </DialogHeader>

        <Tabs
          defaultValue="library"
          className="flex-1 flex flex-col overflow-hidden"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="library">Library</TabsTrigger>
            <TabsTrigger value="upload">Upload</TabsTrigger>
          </TabsList>

          <TabsContent
            value="library"
            forceMount
            className="flex-1 overflow-hidden flex flex-col gap-4 mt-4 data-[state=inactive]:hidden"
          >
            <div className="flex gap-2">
              <Input
                placeholder="Search files..."
                onChange={(e) =>
                  setFilters([
                    {
                      field: "search",
                      operator: "contains",
                      value: e.target.value,
                    },
                  ])
                }
              />
            </div>
            <div className="flex-1 overflow-auto grid grid-cols-2 md:grid-cols-4 gap-4 p-1">
              {files.map((file) => (
                <div
                  key={file.id}
                  className="border rounded p-2 cursor-pointer hover:border-primary flex flex-col gap-2"
                  onClick={() => {
                    onSelect(file);
                    setOpen(false);
                  }}
                >
                  <div className="aspect-square w-full bg-gray-100 rounded overflow-hidden flex items-center justify-center">
                    {file.mimeType.startsWith("image") ? (
                      <img
                        src={file.url}
                        alt={file.originalName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-500 text-xs">
                        {file.mimeType}
                      </span>
                    )}
                  </div>
                  <p className="text-xs truncate" title={file.originalName}>
                    {file.originalName}
                  </p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="upload" forceMount className="mt-4 data-[state=inactive]:hidden">
            <MediaUploader
              onUploadComplete={(file) => {
                onSelect(file);
                setOpen(false);
                // Ideally refetch library list here if we want to stay open
              }}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
